import { useReducer } from 'react';
import { DialogBox } from '../../atomic/dialog';
import { NumberField, TextField } from '../../atomic/forms';
import { SlideSwitch } from '../../atomic/switches';
import {
    CreateTenantRequest,
    TenantModel,
    UpdateTenantRequest,
} from '../../../models/TenantModel';
import { useAuth } from '../../../contexts/AuthContext';
import { DialogProps, useDialog } from '../../../hooks/useDialog';
import { mutateAsync } from '../../../hooks/useApi';
import { tenantApi } from '../../../APIs/tenants';
import { validateEmail } from '../../user/lib/validate';

type DialogOpts<
    C extends boolean = boolean,
    R extends CreateTenantRequest | UpdateTenantRequest =
        | CreateTenantRequest
        | UpdateTenantRequest,
    T extends string | undefined = string | undefined,
> = {
    isCreate: C;
    request: R;
    tenantId: T;
};

type CreateOpts = DialogOpts<true, CreateTenantRequest, undefined>;
type UpdateOpts = DialogOpts<false, UpdateTenantRequest, string>;

function assertValidBaseRequest(
    request: UpdateTenantRequest | CreateTenantRequest
) {
    if ((request.maxUserLicenses ?? 0) < 1) {
        throw new Error('ユーザー数は１以上である必要があります');
    }
    if ((request.availableStorageSize ?? 0) < 1) {
        throw new Error('ストレージ容量は１以上である必要があります');
    }
}

export function useUpdateTenantDialog() {
    return useDialog<UpdateOpts, UpdateOpts, TenantModel>(
        DialogForm,
        (tenant) => ({
            options: {
                isCreate: false as const,
                request: tenant,
                tenantId: tenant.tenantId,
            },
            async onCommit({ request }, { signal }) {
                assertValidBaseRequest(request);
                const resp = await mutateAsync(
                    tenantApi.mutate.updateTenantForAdmin,
                    request,
                    { signal }
                );
                if (resp.status !== 200 || resp.data.status !== 'success') {
                    throw new Error('エラーが発生しました');
                }
            },
        }),
        []
    );
}

export function useCreateTenantDialog() {
    return useDialog<CreateOpts>(
        DialogForm,
        () => ({
            options: {
                isCreate: true as const,
                request: {
                    email: '',
                    name: '',
                    fileUploadLimitDate: 0,
                    availableStorageSize: 100,
                    maxUserLicenses: 10,
                    maxAiRequest: 100000,
                    upload: true,
                    permission: false,
                    aiRequest: true,
                    deleted: false,
                    taskPaused: false,
                    aiRequestCount: 0,
                    extApi: false,
                    mosaic: false,
                },
                tenantId: undefined,
            },
            async onCommit({ request }, { signal }) {
                if (validateEmail(request.email) === false) {
                    throw new Error('メールアドレスのフォーマットが不正です');
                }
                assertValidBaseRequest(request);
                const resp = await mutateAsync(
                    tenantApi.mutate.createTenantForSuperAdmin,
                    request,
                    { signal }
                );
                if (resp.status !== 200 || resp.data.status !== 'success') {
                    throw new Error('エラーが発生しました');
                }
            },
        }),
        []
    );
}

function DialogForm<T extends DialogOpts>({
    options: { isCreate, request: initialState, tenantId },
    onOK,
    ...props
}: DialogProps<T>) {
    const { isSuperAdmin, user } = useAuth();
    const reducer = (
        state: any,
        action: { field: string; payload: string | number | boolean }
    ): CreateTenantRequest | UpdateTenantRequest => {
        const newState = {
            ...state,
            [action.field]: action.payload,
        };
        return newState;
    };
    const [formState, dispatch] = useReducer(reducer, initialState);

    const onPreOK = () => {
        onOK({
            isCreate,
            request: { ...formState },
            tenantId,
        });
    };
    return (
        <DialogBox
            title={isCreate ? 'テナント作成' : 'テナント更新'}
            onOK={onPreOK}
            {...props}
        >
            <ul className="fields">
                <li>
                    <TextField
                        label="テナント名"
                        value={formState.name}
                        required={true}
                        onChange={(e) =>
                            dispatch({
                                field: 'name',
                                payload: e.currentTarget.value,
                            })
                        }
                    />
                    {isCreate && (
                        <TextField
                            label="メールアドレス"
                            value={(formState as CreateTenantRequest).email}
                            required={true}
                            errorMessage={'エラーです'}
                            onChange={(e) =>
                                dispatch({
                                    field: 'email',
                                    payload: e.currentTarget.value,
                                })
                            }
                        />
                    )}
                    <NumberField
                        label="ストレージ利用量超過時の自動ロック（日)"
                        min={0}
                        value={String(formState.fileUploadLimitDate)}
                        required={true}
                        disabled={!isSuperAdmin()}
                        onChange={(e) =>
                            dispatch({
                                field: 'fileUploadLimitDate',
                                payload: e.currentTarget.value,
                            })
                        }
                    />
                    <NumberField
                        label="最大ストレージサイズ(GB)"
                        min={1}
                        value={String(formState.availableStorageSize)}
                        required={true}
                        disabled={!isSuperAdmin()}
                        onChange={(e) =>
                            dispatch({
                                field: 'availableStorageSize',
                                payload: e.currentTarget.value,
                            })
                        }
                    />
                    <NumberField
                        label="最大ユーザー数"
                        min={1}
                        value={String(formState.maxUserLicenses)}
                        required={true}
                        disabled={!isSuperAdmin()}
                        onChange={(e) =>
                            dispatch({
                                field: 'maxUserLicenses',
                                payload: e.currentTarget.value,
                            })
                        }
                    />
                    <NumberField
                        label="AI実行許可枚数"
                        min={0}
                        value={String(formState.maxAiRequest)}
                        required={true}
                        disabled={!isSuperAdmin()}
                        onChange={(e) =>
                            dispatch({
                                field: 'maxAiRequest',
                                payload: e.currentTarget.value,
                            })
                        }
                    />
                    <SlideSwitch
                        defaultValue={formState.aiRequest}
                        name={'AI実行を許可する'}
                        text="AI実行を許可する"
                        disabled={!isSuperAdmin()}
                        onChange={(e) =>
                            dispatch({
                                field: 'aiRequest',
                                payload: !formState.aiRequest,
                            })
                        }
                    />
                    <SlideSwitch
                        defaultValue={formState.upload}
                        name={'アップロードを許可する'}
                        text="アップロードを許可する"
                        disabled={!isSuperAdmin()}
                        onChange={(e) =>
                            dispatch({
                                field: 'upload',
                                payload: !formState.upload,
                            })
                        }
                    />
                    {!isCreate && (
                        <SlideSwitch
                            defaultValue={formState.deleted}
                            name={'テナントを無効化'}
                            text="テナントを無効化"
                            disabled={
                                !isSuperAdmin() || tenantId === user?.tenantId
                            }
                            onChange={(e) =>
                                dispatch({
                                    field: 'deleted',
                                    payload: !formState.deleted,
                                })
                            }
                        />
                    )}
                    <SlideSwitch
                        defaultValue={formState.taskPaused ?? undefined}
                        name="タスク実行中止"
                        text="タスク実行中止"
                        disabled={!isSuperAdmin()}
                        onChange={(e) =>
                            dispatch({
                                field: 'taskPaused',
                                payload: !formState.taskPaused,
                            })
                        }
                    />
                    <hr />
                    <SlideSwitch
                        defaultValue={formState.permission}
                        name={'ユーザグループ'}
                        text="ユーザグループ"
                        disabled={!isSuperAdmin()}
                        onChange={(e) =>
                            dispatch({
                                field: 'permission',
                                payload: !formState.permission,
                            })
                        }
                    />
                </li>
            </ul>
        </DialogBox>
    );
}
